import React, { useCallback, useMemo, useState } from "react";
import clsx from "clsx";

import uniqueId from "lodash.uniqueid";
import moment from "moment";

import { HvDatePicker, HvWarningText } from "@hitachivantara/uikit-react-core";
import { HvTimePicker } from "@hitachivantara/uikit-react-lab";

import Context from "../Context";
import { DateTimeRange, DateTimeStrings, ValueComponentProps } from "../types";
import { padTime, parseDate, parseTime } from "../utils";

import { useStyles } from "./styles";

interface DateTimeValueProps extends ValueComponentProps {
  operator: string;
  value?: DateTimeStrings | DateTimeRange;
}

function valueIsRange(
  operator: string,
  value: DateTimeStrings | DateTimeRange
): value is DateTimeRange {
  return operator === "range";
}

const DateTimeValue = ({
  id,
  operator,
  value: valueProp = {},
  initialTouched = false,
}: DateTimeValueProps): JSX.Element => {
  const classes = useStyles();

  const isRange = valueIsRange(operator, valueProp);

  const context = React.useContext(Context);
  const { labels, dispatchAction } = context;

  const elementId = uniqueId(`datetime${id}`);

  const [touchedDate, setTouchedDate] = useState(initialTouched);
  const [touchedTime, setTouchedTime] = useState(initialTouched);
  const [touchedEndDate, setTouchedEndDate] = useState(initialTouched);
  const [touchedEndTime, setTouchedEndTime] = useState(initialTouched);

  const onDateChange = useCallback(
    (data) => {
      setTouchedDate(true);

      let date;
      if (data != null) {
        date = moment(data).format("YYYY-MM-DD");
      }

      const oldValue = !isRange
        ? (valueProp as DateTimeStrings)?.date
        : (valueProp as DateTimeRange)?.start?.date;

      if (date !== oldValue) {
        let value;
        if (!isRange) {
          value = {
            date,
            time: (valueProp as DateTimeStrings)?.time,
          };
        } else {
          value = {
            start: {
              date,
              time: (valueProp as DateTimeRange)?.start?.time,
            },
            end: (valueProp as DateTimeRange)?.end,
          };
        }

        dispatchAction({
          type: "set-value",
          id,
          value,
        });
      }
    },
    [dispatchAction, id, isRange, valueProp]
  );

  const onTimeChange = useCallback(
    (data) => {
      setTouchedTime(true);

      let time;
      if (data != null) {
        time = `${padTime(data.hours)}:${padTime(data.minutes)}:${padTime(
          data.seconds
        )}`;
      }

      const oldValue = !isRange
        ? (valueProp as DateTimeStrings)?.time
        : (valueProp as DateTimeRange)?.start?.time;

      if (time !== oldValue) {
        let value;
        if (!isRange) {
          value = {
            date: (valueProp as DateTimeStrings)?.date,
            time,
          };
        } else {
          value = {
            start: {
              date: (valueProp as DateTimeRange)?.start?.date,
              time,
            },
            end: (valueProp as DateTimeRange)?.end,
          };
        }

        dispatchAction({
          type: "set-value",
          id,
          value,
        });
      }
    },
    [dispatchAction, id, isRange, valueProp]
  );

  const onEndDateChange = useCallback(
    (data) => {
      setTouchedEndDate(true);

      let date;
      if (data != null) {
        date = moment(data).format("YYYY-MM-DD");
      }

      if (date !== (valueProp as DateTimeRange)?.end?.date) {
        const value = {
          start: (valueProp as DateTimeRange)?.start,
          end: {
            date,
            time: (valueProp as DateTimeRange)?.end?.time,
          },
        };

        dispatchAction({
          type: "set-value",
          id,
          value,
        });
      }
    },
    [dispatchAction, id, valueProp]
  );

  const onEndTimeChange = useCallback(
    (data) => {
      setTouchedEndTime(true);

      let time;
      if (data != null) {
        time = `${padTime(data.hours)}:${padTime(data.minutes)}:${padTime(
          data.seconds
        )}`;
      }

      if (time !== (valueProp as DateTimeRange)?.end?.time) {
        const value = {
          start: (valueProp as DateTimeRange)?.start,
          end: {
            date: (valueProp as DateTimeRange)?.end?.date,
            time,
          },
        };

        dispatchAction({
          type: "set-value",
          id,
          value,
        });
      }
    },
    [dispatchAction, id, valueProp]
  );

  const startDate = isRange
    ? (valueProp as DateTimeRange)?.start?.date
    : (valueProp as DateTimeStrings)?.date;
  const datePickerValue = useMemo(() => parseDate(startDate), [startDate]);

  const startTime =
    (isRange
      ? (valueProp as DateTimeRange)?.start?.time
      : (valueProp as DateTimeStrings)?.time) ?? "";
  const timePickerValue = useMemo(() => parseTime(startTime), [startTime]);

  const endDate = isRange ? (valueProp as DateTimeRange)?.end?.date : null;
  const endDatePickerValue = useMemo(() => parseDate(endDate), [endDate]);

  const endTime = isRange ? (valueProp as DateTimeRange)?.end?.time : null;
  const endTimePickerValue = useMemo(() => parseTime(endTime), [endTime]);

  const dateStatus = !touchedDate
    ? "standBy"
    : datePickerValue != null
    ? "valid"
    : "invalid";

  const timeStatus = !touchedTime
    ? "standBy"
    : timePickerValue != null
    ? "valid"
    : "invalid";

  const endDateIsBefore =
    startDate != null && endDate != null && endDate < startDate;

  const endTimeIsBeforeOrSame =
    startDate != null &&
    endDate != null &&
    endDate === startDate &&
    startTime != null &&
    endTime != null &&
    endTime <= startTime;

  const endDateTimeIsBefore = endDateIsBefore || endTimeIsBeforeOrSame;

  const endDateStatus = !touchedEndDate
    ? "standBy"
    : endDatePickerValue == null || endDateTimeIsBefore
    ? "invalid"
    : "valid";

  const endTimeStatus = !touchedEndTime
    ? "standBy"
    : endTimePickerValue == null || endDateTimeIsBefore
    ? "invalid"
    : "valid";

  return (
    <div className={classes.root}>
      <div className={clsx(classes.row, classes.horizontal)}>
        <HvDatePicker
          className={classes.datePicker}
          name={`${elementId}-date`}
          required
          status={dateStatus}
          statusMessage={labels.rule.value.datetime.validation.required}
          label={
            isRange
              ? labels.rule.value.datetime.startDateLabel
              : labels.rule.value.datetime.dateLabel
          }
          placeholder={
            isRange
              ? labels.rule.value.datetime.startDatePlaceholder
              : labels.rule.value.datetime.datePlaceholder
          }
          value={datePickerValue}
          onChange={onDateChange}
        />
        <HvTimePicker
          className={classes.timePicker}
          timeFormat="24"
          name={`${elementId}-time`}
          disableDefaultValue
          required
          status={timeStatus}
          statusMessage={labels.rule.value.datetime.validation.required}
          label={
            isRange
              ? labels.rule.value.datetime.startTimeLabel
              : labels.rule.value.datetime.timeLabel
          }
          placeholder={
            isRange
              ? labels.rule.value.datetime.startTimePlaceholder
              : labels.rule.value.datetime.timePlaceholder
          }
          value={timePickerValue}
          onChange={onTimeChange}
          onToggle={(_evt, open) => {
            if (!open && !touchedTime) {
              setTouchedTime(true);
            }
          }}
        />
      </div>
      {isRange && (
        <div className={clsx(classes.row, classes.vertical)}>
          <div className={classes.horizontal}>
            <HvDatePicker
              className={classes.datePicker}
              name={`${elementId}-endDate`}
              required
              status={endDateStatus}
              statusMessage={labels.rule.value.datetime.validation.required}
              aria-errormessage={
                endDateTimeIsBefore ? `${elementId}-combined-error` : undefined
              }
              label={labels.rule.value.datetime.endDateLabel}
              placeholder={labels.rule.value.datetime.endDatePlaceholder}
              value={endDatePickerValue}
              onChange={onEndDateChange}
            />
            <HvTimePicker
              className={classes.timePicker}
              timeFormat="24"
              name={`${elementId}-endTime`}
              disableDefaultValue
              required
              status={endTimeStatus}
              statusMessage={labels.rule.value.datetime.validation.required}
              aria-errormessage={
                endDateTimeIsBefore ? `${elementId}-combined-error` : undefined
              }
              label={labels.rule.value.datetime.endTimeLabel}
              placeholder={labels.rule.value.datetime.endTimePlaceholder}
              value={endTimePickerValue}
              onChange={onEndTimeChange}
              onToggle={(_evt, open) => {
                if (!open && !touchedEndTime) {
                  setTouchedEndTime(true);
                }
              }}
            />
          </div>
          <HvWarningText
            disableBorder
            id={`${elementId}-combined-error`}
            isVisible={endDateTimeIsBefore}
          >
            {labels.rule.value.datetime.validation.invalidInterval}
          </HvWarningText>
        </div>
      )}
    </div>
  );
};

export default React.memo(DateTimeValue);
